import {
  Id,
  SpaceTrackRow,
  SpaceTrackRowSummary,
  Track,
} from "@highnote/server/src/core/entities";
import { ViewingSpaceId } from "App/modules/viewingSpace";

/**
 * What any tile-rendering path may legally receive: a canonical `Track`
 * (Search, Library, the legacy space query) OR a row-backed tile whose
 * canonical doc has not arrived.
 *
 * Structurally a `Track`, deliberately. Existing row consumers
 * (`EntityPreview`, `DefaultTrackActions`, `useTrackFiles`, ...) read
 * `row.entity` as a `Track` and dereference its fields unconditionally, so
 * this keeps Track's own field names rather than exposing the row's
 * (differently-named) ones to those consumers.
 *
 * The three fields below are REQUIRED on `Track` and genuinely absent on a
 * row-only tile, so they are re-declared optional. Without that, the type
 * asserts `versionFilesV2: Id[]` while the value is `undefined`, and every
 * unguarded `.length` / `.every()` / `.map()` type-checks and then throws.
 *
 * A plain `Track` is assignable to this type — required satisfies optional —
 * so widening a signature from `Track` to `RenderableTrack` never breaks an
 * existing caller. It only makes the absent case visible to the compiler.
 *
 * Prefer this over `Track` on any signature reachable from
 * `buildSpaceTrackTile`. Declaring `Track` there is what forced the
 * `(track as SpaceTrackTile)` casts this type removes: the signature claimed
 * a guarantee the caller could not make.
 */
export type RenderableTrack = Omit<
  Track,
  "versionFilesV2" | "files" | "spaceId"
> & {
  // Only the canonical doc carries the lists. A row carries `versionCount`
  // and `attachmentCount` instead (below).
  versionFilesV2?: Id[];
  files?: Id[];
  // The ORIGIN space. A row-only tile fills this from `row.originSpaceId`,
  // so it is normally present; a tile built from a row written before that
  // field existed still has none. Reading it unguarded is what produced
  // `/space/undefined/...` deeplinks, so it stays optional.
  spaceId?: Id;
  // The space this TILE is displayed under — never the track's own
  // `spaceId` (its origin, which may be a space the viewer can't even read).
  //
  // Branded: this value flows into the file callables further up the stack,
  // where sending an origin silently denies a borrower and disarms the
  // delete guard. See App/modules/viewingSpace.ts.
  viewingSpaceId?: ViewingSpaceId;
  // The row's per-space comment summary (`SpaceTrackRow.summary`).
  // Present only when the tile is row-backed.
  rowSummary?: SpaceTrackRowSummary;
  // True when a `space-tracks-v1` row contributed. Optional here so a plain
  // `Track` satisfies the type; `SpaceTrackTile` below requires it.
  //
  // NOTE this is TRUE for a space's OWN tracks as well: every track has an
  // origin row. It says "a row contributed", not "the canonical document is
  // out of reach". For the second question use `isBorrowed`.
  isRowBacked?: boolean;
  // True when the row lives in a space that is NOT the track's origin. Only
  // this one means "there is no canonical `tracks-v2` document this viewer
  // may read" — the rules are origin-only. Anything that drops a live
  // canonical watcher, or reads files by id instead of through GET_TRACK,
  // must key on THIS, never on `isRowBacked`.
  //
  // Optional here for the same reason as `isRowBacked`; required on
  // `SpaceTrackTile` below.
  isBorrowed?: boolean;
  // Row-only summary fields, carried under their own names since Track has
  // no counterparts. Render sites that lack a canonical
  // `versionFilesV2`/`files` array fall back to these.
  versionCount?: number;
  attachmentCount?: number;
  defaultVersionFileId?: Id;
};

/**
 * Exactly what `buildSpaceTrackTile` returns — `RenderableTrack` plus a
 * guaranteed `isRowBacked` and `isBorrowed`, because the builder always
 * decides both.
 *
 * Take `RenderableTrack` on a signature. Return `SpaceTrackTile` from the
 * builder.
 *
 * NOT the client's existing `SpaceTrack` (`EntityTable/config.ts`), which is
 * `Track & { isSpaceTrack; index }` — an unrelated type.
 */
export type SpaceTrackTile = RenderableTrack & {
  isRowBacked: boolean;
  isBorrowed: boolean;
};

/**
 * The four PER-SPACE ALIAS fields, read from a row onto a track-shaped
 * object. When a row exists it is the truth for these four (decision 3,
 * 2026-09-07): each row owns its own copy from creation on, and the server
 * never re-mirrors them from `tracks-v2`. With no row, the track is returned
 * as-is — same object, so callers can use reference equality.
 *
 * `null` on the row means "cleared in this space" and becomes `undefined`
 * here, because every consumer of `Track` already treats these fields as
 * optional and checks them with `||` / `?.`.
 *
 * `description` alone distinguishes ABSENT from `null`: a row written before
 * the field existed (not yet backfilled) has no `description` key at all,
 * and that must not hide the track's own text. Absent → the track's value;
 * `null` → cleared. The backfill writes `null` explicitly, so after it runs
 * the row is always the truth.
 */
export const applyRowAliases = <T extends Partial<Track>>(
  track: T,
  row?: SpaceTrackRow,
): T => {
  if (!row) return track;
  return {
    ...track,
    title: row.displayTitle,
    // No key at all when the row has none, so a caller that spreads this
    // result over a track (buildSpaceTrackTile) keeps the track's value.
    ...(row.description !== undefined
      ? { description: row.description ?? undefined }
      : {}),
    artistName: row.artistName ?? undefined,
    artworkFile: row.artworkFileId ?? undefined,
  };
};

/**
 * Builds a Track-shaped tile for a space's child-track list from a
 * `space-tracks-v1` row, the canonical Track doc, or both. Pure — no hook,
 * no React, no store reads. Callers resolve `row`/`track` from whichever
 * store slice(s) they hold and pass them in.
 *
 * - row only (the common case right after a track is borrowed into a
 *   space, before its canonical doc has been fetched by anything): the
 *   tile is built ENTIRELY from the row's mirrored header fields,
 *   normalized onto Track's own field names (`displayTitle` -> `title`,
 *   `artworkFileId` -> `artworkFile`, plus `artistName`, `durationMs`,
 *   and the row-only summary fields `versionCount`, `attachmentCount`,
 *   `defaultVersionFileId`, carried under their own names since Track has
 *   no counterparts). The tile has NO `versionFilesV2`/`files` — those
 *   only exist on the canonical doc.
 * - track only (no row — e.g. Search, Library, or any other non-row-backed
 *   call site): the tile is the track, untouched.
 * - both: identity (`id`) and the mirrored header fields come from the
 *   ROW — the same values render whether or not the canonical doc has
 *   loaded, so a tile never visibly "jumps" as the doc arrives. Everything
 *   the row doesn't carry (`versionFilesV2`, `files`, `description`, ...)
 *   comes from the canonical `track`.
 * - neither: `undefined` — there is nothing to render.
 */
export const buildSpaceTrackTile = ({
  row,
  track,
  viewingSpaceId,
}: {
  row?: SpaceTrackRow;
  track?: Track;
  viewingSpaceId?: ViewingSpaceId;
}): SpaceTrackTile | undefined => {
  if (!row && !track) return undefined;

  return {
    ...track,
    ...(row && {
      // IDENTITY — the row's own trackId, NEVER `row.id` (the composite
      // `${spaceId}_${trackId}` document id). A leaked composite here
      // flows into `EntityRowConfig.id`/`.entity.id`, then into
      // `selectedEntities`, then into every bulk move/remove/hard-delete
      // (SpaceItems.tsx's onSelectRow).
      id: row.trackId,
      createdBy: row.createdBy,
      createdAt: row.createdAt,
      // HEADER — normalized onto Track's own field names (aliases first).
      ...applyRowAliases({}, row),
      durationMs: row.durationMs ?? undefined,
      versionCount: row.versionCount,
      attachmentCount: row.attachmentCount,
      defaultVersionFileId: row.defaultVersionFileId ?? undefined,
      rowSummary: row.summary,
      ...(!track && {
        // Row-only. The row IS the content. When a canonical document is
        // present it is fresher than the mirror by one trigger hop, so the
        // spread of `track` above wins and none of this applies.
        spaceId: row.originSpaceId,
        defaultVersionId: row.defaultVersionFileId ?? undefined,
        ...(row.versionFileIds && {
          versionFilesV2: [...row.versionFileIds],
        }),
        ...(row.pinnedVersionFileIds && {
          pinnedVersionFiles: [...row.pinnedVersionFileIds],
        }),
        ...(row.attachmentFileIds && { files: [...row.attachmentFileIds] }),
        ...(row.fileUpdatedAt != null && { fileUpdatedAt: row.fileUpdatedAt }),
      }),
    }),
    viewingSpaceId,
    isRowBacked: Boolean(row),
    // A row whose space is not the track's origin. See the field's comment
    // on `RenderableTrack`: this, not `isRowBacked`, is the question
    // "is the canonical document out of this viewer's reach?".
    isBorrowed: Boolean(row) && row.originSpaceId !== row.spaceId,
  } as SpaceTrackTile;
};

/**
 * True when a `RenderableTrack` carries the canonical doc's own fields, and
 * can therefore be handed to code that legitimately requires a full `Track`.
 *
 * This is a type predicate rather than a cast, so the narrowing is backed by
 * a real runtime check. Use it at the boundary of anything that needs the
 * version list, the attachment list, or the origin space — the track editor,
 * the version editor, and the download paths all do.
 *
 * A false result is NOT an error state. It means the canonical doc has not
 * arrived. On a space page it may never arrive: `globalTracks.tracks` is
 * filled by the legacy space query (which never returns a borrowed track)
 * and by the track page's own watcher, so a borrowed track's tile stays
 * row-only for as long as the user stays on the space page. Hide or disable
 * the affordance rather than letting it open empty.
 */
export const isCanonicalTrack = (track: RenderableTrack): track is Track =>
  Boolean(track.versionFilesV2 && track.files && track.spaceId);
